import { Card } from '@shared/components/ui';
import { Cloud, HardDrive } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StorageProps {
  data: {
    storage_destination_id: string;
  };
  onChange: (data: unknown) => void;
  onNext: () => void;
  onBack: () => void;
}

interface StorageDestination {
  id: string;
  name: string;
  type: string;
  is_default: boolean;
  config: unknown;
}

export function Storage({ data, onChange, onNext: _onNext, onBack: _onBack }: StorageProps) {
  const { t } = useTranslation('backups');
  const [destinations, setDestinations] = useState<StorageDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ storage_destination_id?: string }>({});

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/v1/storage', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setDestinations(data.destinations || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching destinations:', e);
    } finally {
      setLoading(false);
    }
  };

  const activeDestinations = destinations;

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1">
          {t('wizard.storage.selectDestination')}
        </label>
        {loading ? (
          <div className="flex items-center justify-center p-12 border border-surface-border-light dark:border-surface-border-dark rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : activeDestinations.length === 0 ? (
          <div className="p-6 border-2 border-dashed border-border-strong-light dark:border-border-strong-dark rounded-xl text-center">
            <Cloud
              size={40}
              className="mx-auto text-surface-border-light dark:text-surface-border-dark mb-3"
            />
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
              {t('wizard.storage.noDestinations')}
            </p>
            <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
              {t('wizard.storage.configureFirst')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {activeDestinations.map((destination) => (
              <button
                key={destination.id}
                type="button"
                onClick={() => {
                  onChange({
                    ...data,
                    storage_destination_id: destination.id,
                    storage_destination_name: destination.name,
                  });
                  setErrors({});
                }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  data.storage_destination_id === destination.id
                    ? 'border-brand-600 bg-accent-soft-light dark:bg-accent-soft-dark'
                    : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <HardDrive
                      className={
                        data.storage_destination_id === destination.id
                          ? 'text-brand-600 dark:text-brand-400'
                          : 'text-text-subtle-light dark:text-text-subtle-dark'
                      }
                      size={24}
                      strokeWidth={2}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-900 dark:text-white">{destination.name}</p>
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark mt-1">
                      {destination.type}
                    </p>
                  </div>
                  {data.storage_destination_id === destination.id && (
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 rounded-full bg-brand-600 flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        {errors.storage_destination_id && (
          <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
            {errors.storage_destination_id}
          </p>
        )}
      </div>

      <Card className="p-4 bg-surface-2-light dark:bg-surface-card-dark border-surface-border-light dark:border-surface-border-dark">
        <p className="text-xs font-medium text-slate-600 dark:text-text-subtle-dark leading-relaxed">
          {t('wizard.storage.infoText')}
        </p>
      </Card>
    </div>
  );
}
